import { useEffect, useMemo, useRef, useState } from "react";

import { usePlaidLink } from "react-plaid-link";
import { useHistory, useLocation } from "react-router";

import {
  useGetAllRelationships,
  useGetOwnerDetails,
  useGetPlaidLinkToken,
} from "~/v2/api/hooks";
import { useIsBrowserFullscreen } from "~/v2/hooks/useIsBrowserFullscreen";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { useGetPaths } from "../useGetPath";
import type { PlaidStatus } from "./PlaidStatusPanel.pres";
import {
  clearPlaidOAuthSession,
  savePlaidOAuthSession,
} from "./plaidOAuthSession";
import {
  capturePlaidError,
  trackOAuthSessionSaveResult,
} from "./plaidTelemetry";
import { createPlaidEventHandlers } from "./usePlaidLinkEvents";
import { useSubmitPlaidRelationship } from "./useSubmitPlaidRelationship";

/**
 * Fresh bank-link page controller. Single-purpose: always fetches a fresh link
 * token (NO OAuth resume gating, NO sessionEpoch memo, NO resume effects). Any
 * bank may be OAuth, so we still persist the session before opening so the
 * SAME link_token can be resumed when Plaid redirects back to the dedicated
 * OAuth-return page.
 */
export const useLinkACHController = () => {
  const history = useHistory();
  const { search: locationSearch } = useLocation();
  const paths = useGetPaths("ACH");

  const [status, setStatus] = useState<PlaidStatus>("idle");

  const { currentAccount } = useAccountContext({ liveOnly: true });
  const accountID = currentAccount?.id ?? "";

  const { details } = useGetOwnerDetails();

  // Browser full-screen breaks Plaid's OAuth pop-up handoff, so the CTA is
  // blocked until the user leaves it (PRIME-584). Reactive, so exiting
  // full-screen re-enables the button without a reload.
  const isFullscreen = useIsBrowserFullscreen();

  const { ach } = useGetAllRelationships({
    account: currentAccount,
    countryOfTaxResidence: details?.country_of_tax_residence || "",
  });
  const achRelationship = ach?.relationships?.at(0);

  // usePlaidLink captures callbacks at init and won't update them when deps
  // change. A ref keeps the value current inside the stale onSuccess closure.
  const achRelationshipRef = useRef(achRelationship);
  useEffect(() => {
    achRelationshipRef.current = achRelationship;
  }, [achRelationship]);

  const isVerifyingMicroDeposit = useMemo(() => {
    const params = new URLSearchParams(locationSearch);
    return params.get("micro_deposit_verification_required") === "true";
  }, [locationSearch]);

  // Safety net: this fresh-link page must never strand an OAuth resume. If it
  // ever loads with oauth_state_id in the URL (pre-repoint redirect_uri, or a
  // manual nav), bounce to the dedicated OAuth-return page preserving the FULL
  // query string so the resume can complete there. Snapshot once so a later
  // history.replace can't null it out mid-flight.
  const oauthStateIdRef = useRef(
    new URLSearchParams(window?.location?.search ?? "").get("oauth_state_id") ??
      "",
  );
  const isRedirectingToOAuth = !!oauthStateIdRef.current;

  useEffect(() => {
    if (!isRedirectingToOAuth) {
      return;
    }
    history.replace(
      `${Path.ROUTE_FUNDING_LINK_ACH_OAUTH}${window.location.search}`,
    );
  }, [isRedirectingToOAuth, history]);

  // Fetch a fresh token for the normal link flow. Skip it while bouncing an
  // errant OAuth return: the dedicated resume page reuses the PERSISTED token,
  // so a fetch here is a throwaway /link/token/create. Skipping it also keeps
  // linkToken null, so usePlaidLink below never spins up a Link handler on a
  // render that is about to unmount.
  const { data: plaidLinkToken } = useGetPlaidLinkToken({
    accountID,
    options: {
      enabled: !!accountID && !isRedirectingToOAuth,
      refetchOnMount: "always",
    },
  });

  const linkToken = plaidLinkToken?.link_token ?? null;

  const { handlePlaidSuccess } = useSubmitPlaidRelationship({
    accountID,
    getCurrentRelationship: () => achRelationshipRef.current,
    onStatusChange: setStatus,
    clearSession: () => clearPlaidOAuthSession(accountID),
    onCompleted: () => {
      setTimeout(() => {
        history.push(paths.next);
      }, 1000);
    },
  });

  const { onEvent, onExit } = createPlaidEventHandlers({
    accountID,
    // No resume session is live across a non-redirect exit, but clear defensively
    // so a session saved before an aborted open never lingers.
    beforeExit: () => clearPlaidOAuthSession(accountID),
    onExitClean: () => history.push(paths.back),
    onExitError: () => history.push(paths.back),
  });

  const { open: openPlaid, ready: isPlaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: handlePlaidSuccess,
    onExit,
    onEvent,
    userLegalName: currentAccount?.name,
    userEmailAddress: currentAccount?.email,
  });

  const onBackClicked = () => {
    history.push(paths.back);
  };

  const onOpenPlaidClicked = () => {
    // Persist the session BEFORE the OAuth handoff so we can resume the SAME
    // link_token when Plaid redirects the page back with oauth_state_id (which
    // lands on the dedicated OAuth-return page).
    if (plaidLinkToken?.link_token) {
      const saved = savePlaidOAuthSession({
        link_token: plaidLinkToken.link_token,
        expiration: plaidLinkToken.expiration,
        accountID,
        search: window.location.search,
        savedAt: Date.now(),
      });
      // A failed save (private mode / blocked storage) guarantees that an OAuth
      // bank will come back as an unresumable "orphan". Track it here so that
      // drop-off is attributable to storage rather than looking like a mystery.
      trackOAuthSessionSaveResult(saved);
      if (!saved) {
        capturePlaidError(
          accountID,
          "Failed to persist Plaid OAuth resume session",
        );
      }
    }
    setStatus("plaid_open");
    openPlaid();
  };

  return {
    status,
    isPlaidReady,
    isVerifyingMicroDeposit,
    showAlreadyLinked: !!achRelationship,
    isFullscreen,
    onOpenPlaid: onOpenPlaidClicked,
    onBack: onBackClicked,
    // Connector gating
    isLoading: !currentAccount || !details || ach.isLoading,
    isRedirectingToOAuth,
  };
};

export type UseLinkACHControllerReturn = ReturnType<
  typeof useLinkACHController
>;
